import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { produce, Draft } from "immer";
import {
  GetCityApi,
  GetMasterApi,
  getProfileApi,
  GetStateApi,
} from "../../api/services";

interface State {
  openModal: boolean;
  generateAbha: {
    aadharcard: string | number;
    number: string | number;
  };
  master: masterTypes;
  loading: boolean;
  stateData: StateType[];
  cityData: CityType[];
}

export const VaccinationReducer = createAsyncThunk<
  State["master"]["vaccinations"],
  { path: string }
>("profile/VaccinationReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["vaccinations"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const MedicationsReducer = createAsyncThunk<
  State["master"]["medications"],
  { path: string }
>("profile/MedicationsReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["medications"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
export const healthProblemsReducer = createAsyncThunk<
  State["master"]["healthProblems"],
  { path: string }
>("profile/healthProblemsReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["healthProblems"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
export const symptomsReducer = createAsyncThunk<
  State["master"]["symptoms"],
  { path: string }
>("profile/symptomsReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["symptoms"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
export const allergiesReducer = createAsyncThunk<
  State["master"]["allergies"],
  { path: string }
>("profile/allergiesReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["allergies"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
export const familyRelationReducer = createAsyncThunk<
  State["master"]["familyRelation"],
  { path: string }
>("profile/familyRelationReducer", async (data, thunkAPI) => {
  try {
    const result = await GetMasterApi(data);
    if (result?.success) {
      return result?.data as State["master"]["familyRelation"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});
export const getStateReducer = createAsyncThunk<State["stateData"]>(
  "profile/getStateReducer",
  async (data, thunkAPI) => {
    try {
      const result = await GetStateApi();
      if (result?.success) {
        return result?.states as State["stateData"];
      } else {
        throw new Error("Failed to fetch data");
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const getCiyReducer = createAsyncThunk<
  State["cityData"],
  { state_id: number }
>("profile/getCiyReducer", async (data, thunkAPI) => {
  try {
    const result = await GetCityApi(data);
    if (result?.success) {
      return result?.cities as State["cityData"];
    } else {
      throw new Error("Failed to fetch data");
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

const initialState: State = {
  openModal: false,
  generateAbha: {
    aadharcard: "",
    number: "",
  },
  master: {
    vaccinations: [],
    medications: [],
    healthProblems: [],
    symptoms: [],
    allergies: [],
    familyRelation: [],
  },
  loading: false,
  stateData: [],
  cityData: [],
};

const ProfileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    updateGenerateAbha: (state, action: PayloadAction) => {},
    handleOpenModal: (state) => {
      state.openModal = true;
    },
    handleCloseModal: (state) => {
      state.openModal = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      VaccinationReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["vaccinations"] | undefined>
      ) => {
        state.master.vaccinations = action.payload ?? [];
      }
    );
    builder.addCase(
      MedicationsReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["medications"] | undefined>
      ) => {
        state.master.medications = action.payload ?? [];
      }
    );
    builder.addCase(
      healthProblemsReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["healthProblems"] | undefined>
      ) => {
        state.master.healthProblems = action.payload ?? [];
      }
    );
    builder.addCase(
      symptomsReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["symptoms"] | undefined>
      ) => {
        state.master.symptoms = action.payload ?? [];
      }
    );
    builder.addCase(
      allergiesReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["allergies"] | undefined>
      ) => {
        state.master.allergies = action.payload ?? [];
      }
    );
    builder.addCase(
      familyRelationReducer.fulfilled,
      (
        state,
        action: PayloadAction<State["master"]["familyRelation"] | undefined>
      ) => {
        state.master.familyRelation = action.payload ?? [];
      }
    );
    builder.addCase(
      getStateReducer.pending,
      (state, action: PayloadAction<State["stateData"] | undefined>) => {
        state.stateData = [];
      }
    );
    builder.addCase(
      getStateReducer.fulfilled,
      (state, action: PayloadAction<State["stateData"] | undefined>) => {
        state.stateData = action.payload ?? [];
      }
    );
    builder.addCase(getStateReducer.rejected, (state) => {
      state.stateData = [];
    });
    builder.addCase(getCiyReducer.pending, (state) => {
      state.cityData = [];
    });
    builder.addCase(
      getCiyReducer.fulfilled,
      (state, action: PayloadAction<State["cityData"] | undefined>) => {
        state.cityData = action.payload ?? [];
      }
    );
    builder.addCase(getCiyReducer.rejected, (state) => {
      state.cityData = [];
    });
  },
});

export const {
  handleOpenModal,
  handleCloseModal,
} = ProfileSlice.actions;

export default ProfileSlice;
